import React, { useState, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { Collapse } from '@material-ui/core'
import { useTheme } from '@material-ui/core/styles'
import { useSnackbar } from 'notistack'
import {
  ExpandLessRounded as UpIcon,
  ExpandMoreRounded as DownIcon,
  FileCopyRounded as FileIcon,
} from '@material-ui/icons'

import DateTime from 'luxon/src/datetime.js'

import { objLength } from '../../../utils/customMethods'

import IconButton from '../../atoms/inputs/iconButton'
import Typography from '../../atoms/display/typography'
import EscapeDrag from '../../molecules/display/escapeDrag'

import useStyles from './ConsumerInformationStyle'
import i18n from '../../../i18n'

const ConsumerInformation = (props) => {
  const classes = useStyles()
  const theme = useTheme()

  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()

  const generalInformation = useSelector((state) => state.generalInformation)
  const [expanded, setExpanded] = useState({
    0: false,
  })

  const copyButtonArray = ['id']
  const excludeArray = ['departmentId']

  const handleCollapse = useCallback((index) => {
    setExpanded((expanded) => ({
      ...expanded,
      [index]: !expanded[index],
    }))
  }, [])

  const dynamicTranslation = (value) =>
    i18n.exists(`commons.consumerInformation.${value}`)
      ? t(`commons.consumerInformation.${value}`)
      : value

  const copyToClippboard = (string) => {
    navigator.clipboard.writeText(string).then(
      () => {
        enqueueSnackbar(`${t('commons.copied')}`, {
          variant: 'success',
        })
      },
      (err) => {
        console.error(t('commons.copiedError'), err)
      }
    )
  }

  const formatDate = (value) => {
    const date = new Date(value)
    const format = `D '-' t`
    const time = DateTime.fromJSDate(date).setLocale('pt-br').toFormat(format)

    return isNaN(date.getTime()) ? value : time
  }

  const renderDate = (value) => {
    let format = formatDate(value)
    return (
      <td>
        <Typography variant="body1">
          <EscapeDrag>{format}</EscapeDrag>
        </Typography>
      </td>
    )
  }

  const renderStringNumber = (value, isCopyButton) => {
    let translation = dynamicTranslation(value)
    return (
      <>
        <td>
          <Typography variant="body1">
            <EscapeDrag>{translation}</EscapeDrag>
          </Typography>
        </td>
        {isCopyButton && (
          <td>
            <div className="copyIcon">
              <IconButton
                color="primary"
                size="small"
                onClick={(e) => {
                  e.stopPropagation()
                  copyToClippboard(translation)
                }}
              >
                <FileIcon />
              </IconButton>
            </div>
          </td>
        )}
      </>
    )
  }

  const switchRender = (value, isCopyButton) => {
    let type = Object.prototype.toString.call(value)
    let render = null
    switch (type) {
      case '[object String]':
        if (isNaN(value) && Date.parse(value)) {
          render = renderDate(value)
        } else {
          render = renderStringNumber(value, isCopyButton)
        }
        break
      case '[object Date]':
        render = renderDate(value)
        break
      case '[object Array]':
        render = renderArray(value)
        break
      case '[object Object]':
        render = renderObject(value)
        break
      default:
        render = renderStringNumber(value, isCopyButton)
        break
    }
    return render
  }

  // TODO: improve this if in the future there is an array field
  const renderArray = (array) => array.map((element) => switchRender(element))

  const renderObject = (obj) => {
    return Object.entries(obj).map((e) => {
      if (e[1]) {
        let isCopyButton = copyButtonArray.includes(e[0])
        let isInclude = !excludeArray.includes(e[0])
        if (Object.prototype.toString.call(e[1]) !== '[object Object]') {
          return (
            isInclude && (
              <tr>
                {switchRender(e[0], false)}
                {switchRender(e[1], isCopyButton)}
              </tr>
            )
          )
        } else {
          return switchRender(e[1], isCopyButton)
        }
      }
    })
  }

  const renderItems = () => {
    const chatId = props?.chatId
    const data = generalInformation.conversations[chatId]?.consumerInfo

    return objLength(data) > 0 ? (
      renderObject(data)
    ) : (
      <div className={classes.emptySpace}>{t('items.notfound')}</div>
    )
  }

  const renderContent = () => {
    return (
      <>
        <div className={classes.menuBar} onClick={() => handleCollapse(0)}>
          <Typography variant="body1">
            {t('commons.consumerInformation.label')}
          </Typography>
          <div className={classes.arrowContent}>
            {expanded[0] ? <UpIcon /> : <DownIcon />}
          </div>
        </div>
        <Collapse in={expanded[0]} timeout="auto" unmountOnExit>
          <div className={classes.content}>
            <table width="100%" cellPadding="10">
              <tbody>{renderItems()}</tbody>
            </table>
          </div>
        </Collapse>
      </>
    )
  }

  return renderContent()
}

export default ConsumerInformation
